/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import classnames from "classnames";
import CountUp from "react-countup";
import { useSelector, useDispatch } from "react-redux";
import { APIClient } from "../../helpers/api_helper";
import { setAuthorization, getLoggedinUser } from "../../helpers/api_helper";
import axios from "axios";

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');

const CountUpComponent = ({ end }) => {
  return <CountUp start={0} end={end} duration={3} />;
};

const Widget = ({ widget }) => {
  return (
    <Col xl={3} md={6}>
      <Card>
        <CardBody style={{ height: "100px", overflow: "hidden" }}>
          <div className="d-flex align-items-center counter-widget">
            <div className="flex-grow-1">
              <p className="text-uppercase fw-semibold fs-14 text-muted mb-1">
                {widget.label || "Data"}
              </p>
              <h2 className="mb-0 mr-2">
                <CountUpComponent end={widget.counter || 0} />
              </h2>
            </div>
            <div className="avatar-sm flex-shrink-0">
              <span className="avatar-title bg-light text-primary rounded-circle fs-3">
                <i className={"align-middle " + (widget.badgeIcon || "")}></i>
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

const TileBoxs = () => {
  const [activeTab, setActiveTab] = useState("statistics");
  const [stats, setStats] = useState({
    totalDocuments: 0,
    totalChunks: 0,
    averageChunksPerDocument: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const authUser = getLoggedinUser();
    if (authUser && authUser.token) {
      return {
        'Authorization': `Bearer ${authUser.token}`,
        'Content-Type': 'application/json'
      };
    }
    return {};
  };

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Set authorization headers
      setAuthorization();
      
      // Try to get dashboard statistics from multiple endpoints
      let dashboardData = null;
      
      // First try the dashboard stats endpoint
      try {
        const dashboardResponse = await axios.get(`${API_BASE}/document/dashboard/stats`, {
          headers: getAuthHeaders()
        });
        if (dashboardResponse.data) {
          dashboardData = dashboardResponse.data;
          console.log('✅ Dashboard stats from API:', dashboardData);
          console.log('✅ Using official dashboard statistics endpoint');
        }
      } catch (dashboardErr) {
        console.log('Dashboard stats endpoint not available:', dashboardErr.message);
        console.log('Trying document list as fallback...');
      }
      
      // If dashboard endpoint fails, get data from document list
      if (!dashboardData) {
        console.log('🔄 Using fallback method: calculating stats from document list...');
        const documentsResponse = await axios.get(`${API_BASE}/document/list`, {
          headers: getAuthHeaders(),
          params: { page: 1, pageSize: 1000 } // Get all documents
        });
        
        // Try to get chunk information from a separate endpoint
        let chunkInfo = null;
        try {
          const chunkResponse = await axios.get(`${API_BASE}/document/chunks`, {
            headers: getAuthHeaders()
          });
          if (chunkResponse.data) {
            chunkInfo = chunkResponse.data;
          }
        } catch (chunkErr) {
          console.log('Chunk endpoint not available, will estimate chunks...');
        }
        
        if (documentsResponse.data) {
          let documents = [];
          let totalDocs = 0;
          
          // Handle different response structures
          if (Array.isArray(documentsResponse.data)) {
            documents = documentsResponse.data;
            totalDocs = documents.length;
          } else if (documentsResponse.data.rows && Array.isArray(documentsResponse.data.rows)) {
            documents = documentsResponse.data.rows;
            totalDocs = documentsResponse.data.count || documents.length;
          } else if (documentsResponse.data.data && Array.isArray(documentsResponse.data.data)) {
            documents = documentsResponse.data.data;
            totalDocs = documentsResponse.data.count || documents.length;
          } else if (documentsResponse.data.documents && Array.isArray(documentsResponse.data.documents)) {
            documents = documentsResponse.data.documents;
            totalDocs = documentsResponse.data.count || documents.length;
          }
          
          // Debug: Log first document to see available fields
          if (documents.length > 0) {
            console.log('Sample document structure:', documents[0]);
            console.log('All document fields:', Object.keys(documents[0]));
          }
          
          // Calculate statistics from documents
          const totalChunks = documents.reduce((sum, doc) => {
            // Try multiple possible field names for chunks
            const chunkCount = doc.chunks || doc.chunkCount || doc.chunk_count || doc.numChunks || doc.chunkCount || 0;
            console.log(`Document ${doc.id || doc._id}: chunks = ${chunkCount}`);
            return sum + chunkCount;
          }, 0);
          
          // If no chunks found, estimate based on file size or content
          let finalTotalChunks = totalChunks;
          if (totalChunks === 0 && documents.length > 0) {
            // First try to use chunk info from API if available
            if (chunkInfo && chunkInfo.totalChunks) {
              finalTotalChunks = chunkInfo.totalChunks;
              console.log('Using chunk info from API:', chunkInfo);
            } else {
              // Estimate chunks based on file size or content length
              const estimatedChunks = documents.reduce((sum, doc) => {
                const fileSize = doc.fileSize || doc.size || doc.file_size || 0;
                const contentLength = doc.contentLength || doc.content_length || doc.textLength || 0;
                
                let estimated = 1; // At least 1 chunk per document
                
                if (contentLength > 0) {
                  // Estimate based on content length (1 chunk per 500 characters)
                  estimated = Math.max(1, Math.ceil(contentLength / 500));
                } else if (fileSize > 0) {
                  // Estimate based on file size (1 chunk per 1000 bytes)
                  estimated = Math.max(1, Math.ceil(fileSize / 1000));
                } else {
                  // Default estimation based on document type
                  const fileType = doc.fileName ? doc.fileName.split('.').pop().toLowerCase() : 'unknown';
                  switch (fileType) {
                    case 'pdf':
                      estimated = 5; // PDFs typically have more chunks
                      break;
                    case 'doc':
                    case 'docx':
                      estimated = 3; // Word documents
                      break;
                    case 'txt':
                      estimated = 2; // Text files
                      break;
                    default:
                      estimated = 2; // Default
                  }
                }
                
                console.log(`Document ${doc.id || doc._id}: estimated chunks = ${estimated} (file size: ${fileSize}, content length: ${contentLength})`);
                return sum + estimated;
              }, 0);
              finalTotalChunks = estimatedChunks;
            }
          }
          
          const avgChunks = totalDocs > 0 ? Math.round((finalTotalChunks / totalDocs) * 10) / 10 : 0;
          
          dashboardData = {
            totalDocuments: totalDocs,
            totalChunks: finalTotalChunks,
            averageChunksPerDocument: avgChunks
          };
        }
      }
      
      // Update stats with the data we got
      if (dashboardData) {
        setStats({
          totalDocuments: dashboardData.totalDocuments || dashboardData.total_documents || dashboardData.documents || 0,
          totalChunks: dashboardData.totalChunks || dashboardData.total_chunks || dashboardData.chunks || 0,
          averageChunksPerDocument: dashboardData.averageChunksPerDocument || dashboardData.avg_chunks_per_document || dashboardData.avgChunks || 0,
        });
      }
      
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Failed to load dashboard statistics');
      
      // Set default values on error
      setStats({
        totalDocuments: 0,
        totalChunks: 0,
        averageChunksPerDocument: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const widgetData = useMemo(
    () => [
      {
        id: 1,
        label: "Total Documents",
        badgeIcon: "bx bx-upload",
        counter: stats.totalDocuments,
      },
      {
        id: 2,
        label: "Total Chunks",
        badgeIcon: "bx bx-layer",
        counter: stats.totalChunks,
      },
      {
        id: 3,
        label: "Avg. Chunks / Document",
        badgeIcon: "bx bx-bar-chart-alt-2",
        counter: stats.averageChunksPerDocument,
      },
    ],
    [stats]
  );

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  return (
    <React.Fragment>
      <Nav tabs className="nav nav-tabs nav-border-top nav-border-top-primary mb-4 mt-5">
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "statistics" })}
            onClick={() => toggleTab("statistics")}
          >
            Statistics
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="statistics">
          {loading ? (
            <Row>
              <Col md={12}>
                <Card>
                  <CardBody className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading statistics...</p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          ) : error ? (
            <Row>
              <Col md={12}>
                <Card>
                  <CardBody className="text-center">
                    <i className="fas fa-exclamation-triangle text-warning mb-3" style={{ fontSize: '3rem' }}></i>
                    <h5 className="text-warning">Unable to load statistics</h5>
                    <p className="text-muted">{error}</p>
                    <button 
                      className="btn btn-outline-primary btn-sm"
                      onClick={fetchStats}
                    >
                      <i className="fas fa-sync-alt me-2"></i>
                      Retry
                    </button>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          ) : (
            <Row>
              {widgetData.map((widget) => (
                <Widget key={widget.id} widget={widget} />
              ))}
            </Row>
          )}
        </TabPane>
      </TabContent>
    </React.Fragment>
  );
};

export default TileBoxs;
